<template>
    <b-container fluid v-show="isValidEscript">
        <b-button v-show="showCancelButton" variant="danger" @click="cancelRx">Cancel</b-button>

        <b-dropdown id="renew-dropdown" v-show="showRenewDropdown" variant="info" text="Renew Rx...">
            <b-dropdown-item @click="renewRx(1)">1 Refill</b-dropdown-item>
            <b-dropdown-item @click="renewRx(2)">2 Refills</b-dropdown-item>
            <b-dropdown-item @click="renewRx(3)">3 Refills</b-dropdown-item>
            <b-dropdown-item @click="renewRx(4)">4 Refills</b-dropdown-item>
            <b-dropdown-item @click="renewRx(5)">5 Refills</b-dropdown-item>
            <b-dropdown-item @click="$bvModal.show('custom-renew')">More Refills...</b-dropdown-item>

        </b-dropdown>

        <b-dropdown id="change-dropdown" v-show="showChangeDropdown" variant="warning" text="Request Change">
            <b-dropdown-item @click="changeRx('G')">Generic Substitution</b-dropdown-item>
            <b-dropdown-item @click="changeRx('P')">Prior Authorization Required</b-dropdown-item>
            <b-dropdown-item @click="changeRx('T')">Therapeutic Interchange</b-dropdown-item>
            <b-dropdown-item @click="changeRx('D')">Drug Use Evaluation</b-dropdown-item>
            <b-dropdown-item @click="changeRx('S')">Script Clarification</b-dropdown-item>
            <b-dropdown-item @click="changeRx('OS')">Out of Stock</b-dropdown-item>
            <b-dropdown-item @click="changeRx('U')">Prescriber Authorization</b-dropdown-item>
        </b-dropdown>

        <b-modal id="change-rx-modal" size="xl" hide-header-close no-close-on-backdrop title="Change Rx"
            @hidden="clearChangeRx" @ok="submitChangeRx">
            <b-row>
                <b-col>
                    <!-- Drug selector-->
                    <SearchComponent v-model="object" label="Drug" search-u-r-l="/Drug" :option-label="drugLabel"
                        :id="object && object.id" :paramsInGetById="{ getProgram: true }"></SearchComponent>
                </b-col>
                <b-col cols="2">
                    <b-button variant="success" @click="addRequestedMedication">Add</b-button>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <ul id="requested-meds-list">
                        <li v-for="drug in rxChange.requestedMedications" :key="drug.ndc">
                            <b-row>
                                <b-col>{{ drugLabel(drug.package) }}</b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <label for="change-quantity">Quantity:</label>
                                    <b-form-input id="change-quantity" v-model.number="drug.quantity"
                                        type="number"></b-form-input>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <label for="change-note-textarea">Note: </label>
                                    <AutoCompleteTextarea id="change-note-textarea" class="textarea-autosize" size="sm"
                                        v-model="drug.note" />
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <DirectionsComponent id="change-note-textarea" v-model="drug.sig">
                                    </DirectionsComponent>
                                </b-col>
                            </b-row>
                        </li>
                    </ul>
                </b-col>
            </b-row>

        </b-modal>

        <b-modal id="custom-renew" title="Renew Rx" @ok="renewRxCustom" centered no-fade>
            <b-form-group label="Number of Refills">
                <b-input type="number" v-model="customRenewalCount" min="1" />
            </b-form-group>
        </b-modal>
    </b-container>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Prescription } from '@/models/Prescription';
import { Escript } from "@/models/Escript";
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { RxChange, RequestedMed } from '@/models/RxChange';
import DirectionsComponent from "@/components/Prescription/DirectionsComponent.vue";
import SearchComponent from '@/components/SearchComponent.vue';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';

@Component({
    components: {
        DirectionsComponent,
        SearchComponent,
        AutoCompleteTextarea
    }
})
export default class EscriptButtonComponent extends Vue {
    @Prop({ type: Prescription, default: {} }) readonly value!: Prescription;
    @Prop({ type: Escript, default: {} }) readonly escript!: Escript;

    private object: any = null;
    protected customRenewalCount: number | null = null;

    get isValidEscript(): boolean {
        return !isNaN(this.value.rxNumber) && this.value.rfNumber != null && this.value.escriptID != null;
    }
    get showCancelButton(): boolean {
        return this.isValidEscript && this.escript.isCancelRequest;
    }

    get showRenewDropdown(): boolean {
        return this.isValidEscript;
    }

    get showChangeDropdown(): boolean {
        return this.isValidEscript;
    }

    cancelRx() {
        if (!this.value || !this.value.escriptID) return;

        Axios.post(`escript/${this.value.escriptID}/cancel`, this.value)
            .then(response => {
                const rx = Object.assign(new Prescription(), response.data);
                this.$emit("input", rx);
            }).catch(err => this.$notification(NotificationOptions.error(err)));
    }

    renewRxCustom(e: any) {
        if (!this.customRenewalCount || this.customRenewalCount < 0) {
            e.preventDefault();
            this.$notification(NotificationOptions.error("Invalid number of refills."));
            return;
        }

        this.renewRx(this.customRenewalCount);
    }

    renewRx(num: number | null) {
        if (!this.value || !this.value.escriptID) {
            this.$notification(NotificationOptions.error("Missing escript id."));
            return;
        }

        Axios.post(`escript/${this.value.escriptID}/renew/${num}`, this.value)
            .then(response => {
                console.log(`Escript renew response: ${response.data}`);
                this.$notification(NotificationOptions.notificationOptionsPreset("Renewal request sent."
                    , NotificationOptions.NotificationTypes.success));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            });
    }
    private rxChange: RxChange = {
        type: "",
        note: "",
        requestedMedications: []
    };
    addRequestedMedication() {
        if (this.object == null) return;
        var requestedMed = {
            package: this.object,
            note: "",
            quantity: this.value?.quantity,
            sig: this.value?.directions ?? "",
        } as RequestedMed;
        this.rxChange.requestedMedications.push(requestedMed);
        this.object = null;
    }
    changeRx(type: string) {
        if (!this.value || !this.value.escriptID) return;

        this.rxChange.type = type;
        // changeRx Component to be shown modally here?
        this.$bvModal.show("change-rx-modal");

        // set medication requested info here
        // allow up to 9 alternatives (for Therapeutic Interchange)
        // pharmacist note as well

    }

    clearChangeRx() {
        this.rxChange = {
            type: "",
            note: "",
            requestedMedications: []
        };
        this.object = null;
    }

    submitChangeRx(bvModalEvent: any) {
        if (!this.value || !this.value.escriptID) return;
        bvModalEvent.preventDefault();

        Axios.post(`escript/${this.value.escriptID}/change`, this.rxChange)
            .then(response => {
                console.log(`Escript change for ${this.rxChange.type} response: ${response.data}`);
                this.$bvModal.hide("change-rx-modal");
                this.$notification(NotificationOptions.notificationOptionsPreset("Change request successfully submitted.", NotificationOptions.NotificationTypes.success));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            });
    }

    drugLabel(option: any) {
        if (option.ndc) {
            if (option.productNameShort) {
                return `NDC:${option.ndc} - ${option.productNameShort}`;
            }
            return `NDC:${option.ndc} - ${option.productNameLong}`;
        }
        if (option.packageIdentifier) {
            if (option.productNameShort) {
                return `NDC:${option.packageIdentifier.identifier} - ${option.productNameShort}`;
            }
            return `NDC:${option.packageIdentifier.identifier} - ${option.productNameLong}`;
        }
        return option.productNameLong;
    }

}
</script>
